import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/legacy/image';
import chevronLeft from '@assets/images/chevron-left-grey.svg';
import closeWhite from '@assets/images/close-white.svg';
import chevronLeftWhite from '@assets/images/chevron-left-white.svg';
import chevronRightWhite from '@assets/images/chevron-right-white.svg';
import HotelDetailsData from '@customTypes/hotel-details';
import Slide from '@customTypes/slide';
import SwiperCarousel from '../SwiperCarousel/SwiperCarousel';

type Props = {
  showImageGallery: boolean;
  renderImageGallery: boolean;
  setShowImageGallery: Dispatch<SetStateAction<boolean>>;
  hotelDetails: HotelDetailsData;
  carouselSlides: Slide[];
};

export default function ImageGallery({
  showImageGallery,
  renderImageGallery,
  setShowImageGallery,
  hotelDetails,
  carouselSlides,
}: Props) {
  const [startingSlideIndex, setStartingSlideIndex] = useState<number>(1);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>();
  const [renderImageCarousel, setRenderImageCarousel] = useState<boolean>(false);
  const [showImageCarousel, setShowImageCarousel] = useState<boolean>(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const swiperNavigation = {
    prevRef: navigationPrevRef,
    nextRef: navigationNextRef,
  };

  const onImageGalleryClick = (index: number) => {
    setScrollPosition(window.scrollY);
    setRenderImageCarousel(true);
    setShowImageCarousel(true);
    setStartingSlideIndex(index + 1);
  };

  const closeImageCarousel = () => {
    setShowImageCarousel(false);
  };

  const closeImageGallery = () => {
    window?.scrollTo(0, 0);
    setShowImageGallery(false);
  };

  const restoreScroll = useCallback(() => {
    window.scrollTo(0, scrollPosition);
    setScrollPosition(0);
  }, [scrollPosition]);

  useEffect(() => {
    const mainDiv = document.getElementsByTagName('html');
    if (!showImageCarousel) {
      if (mainDiv) {
        mainDiv[0].style.position = 'inherit';
      }
      restoreScroll();
    } else {
      setTimeout(() => {
        if (mainDiv) {
          mainDiv[0].style.position = 'fixed';
        }
      }, 500);
    }
  }, [restoreScroll, showImageCarousel]);

  return (
    <>
      {renderImageGallery && (
        <div
          className={`flex flex-row flex-wrap justify-between absolute overflow-scroll bg-white w-full z-50 gallery ${
            showImageGallery ? 'open' : 'close fixed'
          } d:hidden`}
        >
          <div
            className={`w-full z-100 h-14 bg-white flex items-center pl-6 gallery mt-64px ${
              showImageGallery ? 'open' : 'close absolute'
            }`}
          >
            <div className="relative h-6 w-6">
              <Image src={chevronLeft} layout="fill" alt="left-arrow" onClick={closeImageGallery} />
            </div>
          </div>

          <div className="h-full w-full flex flex-wrap justify-between">
            {hotelDetails.image.map((image, index) => (
              <div className={`relative mb-2 ${index % 3 === 0 ? 'w-full h-260' : 'w-49% h-32'}`}>
                <Image
                  src={image.picture.details.url}
                  layout="fill"
                  alt={hotelDetails.name}
                  onClick={() => onImageGalleryClick(index)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {renderImageCarousel && (
        <div
          className={`flex flex-row flex-wrap fixed overflow-hidden bg-black w-full h-screen justify-center items-center z-100 carousel pb-20 ${
            showImageCarousel ? 'open' : 'close'
          } d:hidden`}
        >
          <div className="absolute top-0 h-4 w-4 mt-5 z-150 left-7">
            <Image src={closeWhite} layout="fill" alt="close" onClick={closeImageCarousel} />
          </div>
          <div className="mb-2 relative w-full h-60">
            <div className="h-auto w-full">
              <SwiperCarousel
                slides={carouselSlides}
                slidesPerView={3}
                carouselClass="dlp-carousel"
                spaceBetween={8}
                alt={hotelDetails.name}
                startingSlideIndex={startingSlideIndex}
                hidePaginationDots
                setCurrentSlideIndex={setCurrentSlideIndex}
                navigation={swiperNavigation}
              />
            </div>
          </div>
          <div className="absolute bottom-0 mb-9 flex justify-between w-52 mx-auto items-center h-10 pb-20">
            <div
              ref={navigationPrevRef}
              className="relative h-3 w-3 z-100 px-2 py-1"
              id="swiper-custom-previous-navigation-button"
            >
              <Image src={chevronLeftWhite} layout="fill" alt="left-arrow" />
            </div>
            <div className="relative text-white">
              {`${currentSlideIndex ?? startingSlideIndex} / ${carouselSlides.length}`}
            </div>
            <div
              ref={navigationNextRef}
              className="relative h-3 w-3 z-100 px-2 py-1"
              id="swiper-custom-next-navigation-button"
            >
              <Image src={chevronRightWhite} layout="fill" alt="right-arrow" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
